import type { ReactNode, FC } from "react";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { SWReactCountryIcons, SWReactIcons } from "@similarweb/icons";
import { UnaryIcon } from "../Icons/UnaryIcon";
import { BinaryIcon } from "../Icons/BinaryIcon";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { translate } from "app/filters/i18nFilter";
import { DiverseItemIcon } from "app/components/DiverseItemIcon/DiverseItemIcon";
import type { IRecentItem, IRecentItemFilter } from "../types";
import { devicesTypes, webSourceIcon } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import {
    Container,
    Details,
    AssetsList,
    FilterContainer,
    FiltersWrapper,
    FilterText,
    LastVisitLabel,
    AssetDetails,
    SectionLabel,
    CardSectionLayout,
    SubSectionLabel,
    FiltersSectionLayout,
    AssetListItem,
    DiverseIconWrapper,
} from "./styledComponents";

interface RecentCardProps {
    height: number;
    mainItem: IRecentItem;
    comparedItems?: Array<IRecentItem>;
    lastVisit?: Date;
    durationFilter: string;
    countryFilter: number;
    targetPageName: string;
    targetPageParams: any;
    assetDetailsComponent?: ReactNode;
    assetTitleColor?: string;
    showFilters?: boolean;
    onCardClick?: () => void;
    webSourceMap?: Record<string, string>;
}

const WebSourceMap: Record<string, string> = {
    [devicesTypes.TOTAL]: "recents.websorces.all.traffic",
    [devicesTypes.MOBILE]: "recents.websources.mobileweb",
    [devicesTypes.DESKTOP]: "recents.websources.desktop",
};

export const RecentCard: FC<RecentCardProps> = (props: RecentCardProps) => {
    const [hovered, setHovered] = useState<boolean>(false);
    const targetState = proNavigator.getState(props.targetPageName);
    const pageTitle = translate(targetState?.pageTitle?.toString()) || "";
    const homeState = proNavigator.getState(targetState?.homeState);
    const parentPageTitle = (homeState?.pageTitle && translate(homeState?.pageTitle?.toString())) || "";
    const { webSourceMap = WebSourceMap } = props;
    const defaultAssetDetailsComponent = (
        <>
            <SubSectionLabel>{pageTitle}</SubSectionLabel>
            <SectionLabel>
                {homeState ? (
                    <>
                        {translate("digitalmarketing.homepage.recents_widget.in")}
                        &nbsp;{parentPageTitle}
                    </>
                ) : null}
            </SectionLabel>
        </>
    );
    const { assetDetailsComponent = defaultAssetDetailsComponent, assetTitleColor, showFilters = true } = props;
    const countryName = useMemo<string>(() => {
        return (props.countryFilter && CountryService.getCountryById(props.countryFilter)?.text) || "";
    }, [props.countryFilter]);

    const durationPhrasing = useMemo<string>(() => {
        if (!props.durationFilter) {
            return "";
        }

        const phrase: string | Array<string> = DurationService.getDurationData(props.durationFilter).forWidget;
        return Array.isArray(phrase) ? phrase?.[0] || phrase.toString() : phrase.toString();
    }, [props.durationFilter]);

    const lastVisitPhrasing = useMemo<string>(() => {
        if (!props?.lastVisit) {
            return "";
        }
        const lastVisit = dayjs(props.lastVisit);
        const now = dayjs().startOf("day");
        const daysDiff = lastVisit.diff(now, "days", true);
        return Math.abs(daysDiff) < 1
            ? translate("digitalmarketing.homepage.recents_widget.today")
            : lastVisit.from(now);
    }, [props.lastVisit]);

    const getWebSourceDisplayName = (webSource: string): string => {
        return translate(webSourceMap?.[webSource]) || "";
    };

    const filters = useMemo<Array<IRecentItemFilter>>(
        () =>
            [
                {
                    text: durationPhrasing,
                    icon: (
                        <SWReactIcons
                            iconName={"daily-ranking"}
                            size="xs"
                        />
                    ),
                    hidden: !durationPhrasing,
                },
                {
                    text: countryName,
                    icon: (
                        <SWReactCountryIcons
                            countryCode={props.countryFilter}
                            size={"xs"}
                        />
                    ),
                    hidden: !countryName,
                },
                {
                    text: getWebSourceDisplayName(props.targetPageParams?.webSource),
                    icon: (
                        <SWReactIcons
                            iconName={webSourceIcon(props.targetPageParams?.webSource as keyof typeof devicesTypes)}
                            size="xs"
                        />
                    ),
                    hidden: !props.targetPageParams?.webSource,
                },
            ].filter((item) => !item.hidden),
        [durationPhrasing, countryName, props.targetPageParams?.webSource],
    );

    const renderItemIcon = (): ReactNode => {
        switch (props.comparedItems?.length ?? 0) {
            case 0:
                return <UnaryIcon item={props.mainItem} />;

            case 1:
                return (
                    <BinaryIcon
                        mainItem={props.mainItem}
                        secondaryItem={props.comparedItems[0]}
                    />
                );

            default:
                return (
                    <DiverseIconWrapper>
                        <DiverseItemIcon
                            mainItem={props.mainItem.icon}
                            extraItemsCount={props.comparedItems.length}
                        />
                    </DiverseIconWrapper>
                );
        }
    };

    const handleCardClick = (): void => {
        const nonEmptyParams = Object.entries(props.targetPageParams ?? {}).filter(([, val]) => !!val);
        const newParamsObj = nonEmptyParams.reduce(
            (obj, curr) => ({
                ...obj,
                [curr[0]]: curr[1],
            }),
            {},
        );

        props?.onCardClick?.();
        proNavigator.go(props.targetPageName, newParamsObj);
    };

    return (
        <Container
            height={props.height}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={handleCardClick}
        >
            <CardSectionLayout>
                {renderItemIcon()}
                <Details>
                    <AssetsList color={assetTitleColor}>
                        <AssetListItem>{props.mainItem.element ?? props.mainItem.name}</AssetListItem>
                        {props.comparedItems?.length > 0 ? (
                            <>
                                <AssetListItem>
                                    {translate("digitalmarketing.homepage.recents_widget.vs")}
                                </AssetListItem>
                                <AssetListItem>
                                    {props.comparedItems[0].element ?? props.comparedItems[0].name}
                                </AssetListItem>
                            </>
                        ) : null}
                        {props.comparedItems?.length > 1 ? (
                            <AssetListItem>
                                +{props.comparedItems.length - 1}&nbsp;
                                {translate("digitalmarketing.homepage.recents_widget.more")}
                            </AssetListItem>
                        ) : null}
                    </AssetsList>
                    <AssetDetails>{assetDetailsComponent}</AssetDetails>
                </Details>
            </CardSectionLayout>
            {showFilters ? (
                <FiltersSectionLayout>
                    {hovered && filters.length ? (
                        <FiltersWrapper>
                            {filters.map((filter) => (
                                <FilterContainer key={filter.text}>
                                    {filter.icon}
                                    <FilterText>{filter.text}</FilterText>
                                </FilterContainer>
                            ))}
                        </FiltersWrapper>
                    ) : (
                        <LastVisitLabel>{lastVisitPhrasing}</LastVisitLabel>
                    )}
                </FiltersSectionLayout>
            ) : null}
        </Container>
    );
};
